import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { AppErrorBoundary } from "@/app/components/AppErrorBoundary";
import { initObservability } from "@/app/observability";
import { AppProviders } from "@/app/providers/AppProviders";
import { bindDeepLinks } from "@/app/lib/deep-links";
import { initI18n } from "@/app/i18n";
import { useAuthStatus } from "@/shared/auth";
import { routeTree } from "./routes/-routeTree.gen";
import "@/styles/main.css";

const router = createRouter({
  routeTree,
  // Filled in per render below. Declared here so route guards can rely on its shape.
  context: { auth: { status: "unknown" } },
  defaultPreload: "intent",
  // A phone is not a browser tab: scroll position on back is expected, not optional.
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

/**
 * Links and notification taps arrive outside React, so they are bound to the ROUTER, not
 * to a component that may not be mounted when the app is cold-started by a link.
 * `history.push` rather than `navigate`, because these paths are strings from the outside
 * world and cannot be proven against the route tree at compile time.
 */
/**
 * Reporting is armed before anything else runs. An error during start-up — a bad
 * environment value, a plugin that is not in the build — is exactly the one nobody can
 * debug from a phone, so it must not be the one that goes unreported.
 */
void initObservability();
initI18n();

bindDeepLinks((path) => router.history.push(path));

function App() {
  // The session comes from the query cache, so the guard and every screen read one answer.
  const { status } = useAuthStatus();
  const sessionIsKnown = status !== "unknown";

  /**
   * THE ROUTER IS NOT MOUNTED UNTIL THE SESSION IS KNOWN, and both halves of that sentence
   * were paid for.
   *
   * A route's context is resolved when the route MATCHES. Handing RouterProvider a new
   * context object afterwards re-renders, but does not re-run `beforeLoad` for routes that
   * already matched — so a router mounted while the session was still "unknown" keeps that
   * answer forever: the app sits on a loading state and an anonymous visitor is never
   * redirected. That is an endless spinner with no error in the console and every request
   * succeeding, which is exactly how it was found.
   *
   * Invalidating on every change fixes the redirect but introduces a worse bug: invalidation
   * re-renders the matched routes, and a form that was showing a failed sign-in loses it. On
   * a slow connection the session resolves AFTER the sign-in screen is usable, so someone
   * who types quickly gets their error message wiped a moment after it appears — silence,
   * with no way to tell what went wrong.
   *
   * Waiting removes both. The wait is invisible on device (the splash screen is still up,
   * see the platform seam) and is one query on the web. Invalidation stays for the
   * transitions that happen LATER — a sign-out, or a session that expires in the background
   * — where re-running the guards is the whole point and nothing is mid-typing.
   */
  useEffect(() => {
    if (sessionIsKnown) void router.invalidate();
  }, [status, sessionIsKnown]);

  if (!sessionIsKnown) return null;

  return <RouterProvider router={router} context={{ auth: { status } }} />;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AppErrorBoundary>
      <AppProviders>
        <App />
      </AppProviders>
    </AppErrorBoundary>
  </StrictMode>,
);
